import { useParams, useRouter, createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTask, useQuizRanking } from "@/lib/api/tasks/hooks";
import { ChevronLeft, Trophy, Medal, Award } from "lucide-react";
import { cn } from "@/lib/utils";

function QuizRankingPage() {
  const params = useParams({ strict: false });
  const router = useRouter();
  const taskId = params.id as string;

  const { data: task, isLoading: isTaskLoading } = useTask(taskId);
  const { data: ranking, isLoading: isRankingLoading } = useQuizRanking(taskId);

  const isLoading = isTaskLoading || isRankingLoading;

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="size-5 text-yellow-500" />;
      case 2:
        return <Medal className="size-5 text-gray-400" />;
      case 3:
        return <Award className="size-5 text-amber-600" />;
      default:
        return (
          <span className="text-sm font-bold text-muted-foreground">
            {rank}
          </span>
        );
    }
  };

  const getRankBackground = (rank: number) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200";
      case 2:
        return "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200";
      case 3:
        return "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200";
      default:
        return "bg-card";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <PageContainer>
      <Header
        title="퀴즈 랭킹"
        left={
          <Button variant="ghost" size="icon" onClick={() => router.history.back()}>
            <ChevronLeft className="size-6" />
          </Button>
        }
      />

      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground">로딩 중...</div>
      ) : !task || task.type !== "quiz" ? (
        <div className="p-8 text-center text-muted-foreground">
          퀴즈를 찾을 수 없습니다.
        </div>
      ) : (
        <>
          {/* 퀴즈 정보 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{task.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>카테고리: {task.category?.name}</p>
              <p>
                페이지 범위: {task.startPage} ~ {task.endPage}
              </p>
              <p>문제 수: {task.questionCount}문제</p>
            </CardContent>
          </Card>

          {/* 랭킹 테이블 */}
          {!ranking || ranking.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              아직 참여한 학생이 없습니다.
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="font-semibold">총 {ranking.length}명 참여</h3>

              {/* 데스크톱 테이블 */}
              <div className="hidden md:block bg-card rounded-xl overflow-hidden">
                <div className="grid grid-cols-[60px_1fr_100px_100px_120px] gap-4 p-4 bg-muted/50 font-medium text-sm">
                  <div className="text-center">순위</div>
                  <div>학생</div>
                  <div className="text-right">점수</div>
                  <div className="text-right">정답률</div>
                  <div className="text-right">참여 시간</div>
                </div>

                {ranking.map((entry) => (
                  <div
                    key={entry.student._id}
                    className={cn(
                      "grid grid-cols-[60px_1fr_100px_100px_120px] gap-4 p-4 border-t items-center",
                      getRankBackground(entry.rank)
                    )}
                  >
                    <div className="flex justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div>
                      <span className="font-medium">{entry.student.name}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ({entry.student.userId})
                      </span>
                    </div>
                    <div className="text-right font-bold">
                      {entry.score.toLocaleString()}점
                    </div>
                    <div className="text-right text-muted-foreground">
                      {Math.round(
                        (entry.correctCount / entry.questionCount) * 100
                      )}
                      %
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      {formatDate(entry.attemptedAt.toString())}
                    </div>
                  </div>
                ))}
              </div>

              {/* 모바일 카드 */}
              <div className="md:hidden space-y-2">
                {ranking.map((entry) => (
                  <div
                    key={entry.student._id}
                    className={cn(
                      "rounded-xl p-4 border",
                      getRankBackground(entry.rank)
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center">
                        {getRankIcon(entry.rank)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{entry.student.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {entry.student.userId}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">
                          {entry.score.toLocaleString()}점
                        </div>
                        <div className="text-xs text-muted-foreground">
                          정답률{" "}
                          {Math.round(
                            (entry.correctCount / entry.questionCount) * 100
                          )}
                          %
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/tasks/ranking/$id")({
  component: QuizRankingPage,
});
